/*
 * File: base.constants.js
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on July 23, 2024 at 10:33 AM
 * Copyright © 2024 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: July 30, 2025 at 9:43 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Roles
 */

export const ROLES = {
	STANDARD: 'standard',
	COMMUNITY: 'community',
	ANALYTICS_ONLY: 'analytics_only',
	LIMITED: 'limited',
	REVIEWER: 'reviewer',
	SPECIALIST: 'specialist',
	ADMIN: 'admin',
	SUPER: 'super',
};


/**
 * General
 */

export const DEFAULT_TIMEZONE = 'America/New_York';
export const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/; // Enforce password with at least 8 char with at least 1 lower case, 1 upper case and 1 digit


/**
 * Versions
 */

export const VERSIONS = {
	TERMS_VERSION: '2.0.1',
	PRIVACY_VERSION: '2.0.1',
	SECURITY_VERSION: '2.0.1',
	ACCESSIBILITY_VERSION: '2.0.1',
	COOKIE_STATEMENT_VERSION: '2.0.1'
};


/**
 * Share Options
 */

export const SHARE_OPTIONS = {
	WHATSAPP: 'whatsapp',
	FACEBOOK: 'facebook',
	LINK: 'link',
	TWITTER: 'twitter',
	LINKEDIN: 'linkedin',
	REDDIT: 'reddit',
	PINTEREST: 'pinterest',
	EMAIL: 'email'
};


/**
 * Available Locales
 */

export const DEFAULT_LOCALE = 'en-US';
export const AVAILABLE_I18N_NAMESPACES = {
	CLIENT: 'client',
	SERVER: 'server'
};
export const AVAILABLE_LOCALES = [ // Note: This is in order of translation priority
	'en',
	'es',
	'ja'
];


/**
 * Available Countries
 */

export const DEFAULT_COUNTRY = 'us';
export const AVAILABLE_COUNTRIES = [
	'us', // United States
	'ar', // Argentina
	'au', // Australia
	'bo', // Bolivia
	'ca', // Canada
	'cl', // Chile
	'co', // Colombia
	'cr', // Costa Rica
	'cu', // Cuba
	'do', // Dominican Republic
	'ec', // Ecuador
	'sv', // El Salvador
	'gq', // Equatorial Guinea
	'gt', // Guatemala
	'hn', // Honduras
	'ie', // Ireland
	'jp', // Japan
	'mx', // Mexico
	'ni', // Nicaragua
	'nz', // New Zealand
	'pa', // Panama
	'py', // Paraguay
	'pe', // Perú
	'pr', // Puerto Rico
	'es', // Spain
	'gb', // United Kingdom
	'uy', // Uruguay
	've', // Venezuela
];


/**
 * Sources
 */

export const SOURCES = {
	STORIES: 'stories',
	STORY: 'story',
	QUESTIONS: 'questions',
	QUESTION: 'question',
	RESOURCES: 'resources',
	RESOURCE: 'resource',
	RESOURCE_LOCALE: 'resource_locale',
	RESOURCE_PROMPT: 'resource_prompt',
	SHARE: 'share',
	SUBMIT: 'submit',
	RECORD: 'record',
	UPDATE: 'update',
	ARTIST: 'artist',
	EXHIBIT: 'exhibit',
	EXHIBITS: 'exhibits',
	GUESTBOOK: 'guestbook',
	MESSAGE: 'message',
	COMMENT: 'comment',
	ALL: 'all',
};


/**
 * Referral Sources
 */

export const REFERRAL_SOURCES = {
	WEBSITE_CARD: 'website_card',
	WEBSITE_FOOTER: 'website_footer',
	EMAIL: 'email',
	QUESTION_ANSWER: 'answer',
	BLOG_POST: 'blog_post'
};


/**
 * Review Statuses
 */

export const REVIEW_STATUS = {
	WAITING_FOR_REVIEW: 'WAITING_FOR_REVIEW',
	IN_REVIEW: 'IN_REVIEW',
	REQUIRES_APPROVAL: 'REQUIRES_APPROVAL',
	REVIEW_COMPLETED: 'REVIEW_COMPLETED',
};


/**
 * Reporting Reasons
 */

export const REPORTING_REASONS = {
	VIOLENCE_HATE: {
		id: 'violence_hate_exploitation',
		label: 'Violence, hate, or exploitation',
	},
	BULLYING: {
		id: 'bullying_unwanted_contact',
		label: 'Bullying or unwanted contact',
	},
	SCAM_FRAUD: {
		id: 'scam_fraud_impersonation',
		label: 'Scam, fraud, or impersonation',
	},
	MISINFORMATION: {
		id: 'false_information',
		label: 'False information',
	},
};


/**
 * UI Mode Options
 */

export const UI_MODE_OPTIONS = {
	LIGHT: 'light',
	DARK: 'dark'
};


/**
 * Cookies
 */

export const SESSION_TOKEN_COOKIE = 'e_s_token';
export const LOCALE_COOKIE = 'pl_locale';
export const COUNTRY_COOKIE = 'pl_country';
export const EMBED_COOKIE = 'pl_embed';
export const UI_MODE_COOKIE = 'ui_mode';
export const SIDEBAR_EXPANDED_COOKIE = 'sidebar_expanded';
export const VIEW_MODE_COOKIE = 's_ui_view_mode';
export const CONSENT_ACTION_TAKEN_COOKIE = 'c_action_taken';
export const CONSENT_PERFORMANCE_ENABLED_COOKIE = 'c_performance_enabled';
export const CONSENT_ANALYTICS_ENABLED_COOKIE = 'c_analytics_enabled';
export const CONSENT_TARGETING_ENABLED_COOKIE = 'c_targeting_enabled';
export const USER_IMPERSONATION_COOKIE = 'user_imp_token';
export const STORIES_VIEWED_COOKIE = 'stories-viewed-config';
export const ACTIVE_STORIES_FILTERS_COOKIE = 'active-s-filters-config';
export const ACTIVE_QUESTIONS_FILTERS_COOKIE = 'active-q-filters-config';
export const ACTIVE_STORIES_MEDIA_COOKIE = 'active-s-media-config';
export const ACTIVE_QUESTIONS_SEARCH_COOKIE = 'active-q-search-config';
export const REACTION_SCHEMA_COOKIE = 'reaction-schema';
export const UPDATES_SCHEMA_COOKIE = 'updates-schema';


/**
 * Headers
 */

export const CLIENT_PLATFORM_HEADER = 'x-ow-client-platform';
export const CLIENT_VERSION_HEADER = 'x-ow-client-version';
export const SESSION_TOKEN_HEADER = 'x-ow-e-s-token';
export const USER_IMPERSONATION_HEADER = 'x-user-imp-token';
export const PARSE_APP_ID_HEADER = 'x-parse-application-id';
export const PARSE_SESSION_TOKEN_HEADER = 'x-parse-session-token';


/**
 * Cookie Domain
 */

export const COOKIE_DOMAIN = (partner) => {
	switch (process.env.ENV) {
		case 'development':
			return null;
		case 'qa':
			return `qa-${partner.base_domain}`;
		case 'staging':
			return `staging-${partner.base_domain}`;
		case 'production':
			if (partner.redirect_to_subdomain) {
				return `${partner.redirect_to_subdomain}.${partner.base_domain}`;
			}
			return partner.base_domain;
		default:
			return null;
	}
};


/**
 * Expirations
 */

export const SESSION_EXPIRATION = 1 * 365 * 24 * 60 * 60 * 1000; // One year
export const CONSENT_COOKIE_EXPIRATION = 1 * 365 * 24 * 60 * 60 * 1000; // One year
export const LOCALE_COOKIE_EXPIRATION = 1 * 365 * 24 * 60 * 60 * 1000; // One year
export const COUNTRY_COOKIE_EXPIRATION = 1 * 365 * 24 * 60 * 60 * 1000; // One year
export const EMBED_COOKIE_EXPIRATION = 1 * 365 * 24 * 60 * 60 * 1000; // One year
export const SESSION_COOKIE_EXPIRATION = 1 * 365 * 24 * 60 * 60 * 1000; // One year
export const UI_MODE_COOKIE_EXPIRATION = 1 * 365 * 24 * 60 * 60 * 1000; // One year
export const IMPERSONATION_EXPIRATION = 2 * 60 * 60 * 1000; // 2 hours


/**
 * Query Parameters
 */

export const ACTIVE_STORIES_FILTERS_QUERY = 'filters';
export const ACTIVE_QUESTIONS_FILTERS_QUERY = 'filters';
export const ACTIVE_STORIES_MEDIA_QUERY = 'media';
export const ACTIVE_QUESTIONS_SEARCH_QUERY = 'search';
export const EMBED_MODE = 'mode';
export const EMBED_PARTNER_ID = 'pid';
export const EMBED_VIEW = 'view';


/**
 * Alert Types
 */

export const ALERT_STYLES = {
	ERROR: 'error',
	INFO: 'info'
};


/**
 * Error Messages
 */

export const GENERAL_ERROR_MESSAGE = 'Something went wrong, try again later!';


/**
 * Error Alerts
 */

export const ERROR_ALERTS = {
	USER_ACCOUNT_INACTIVE: 'This account is not active or has been deleted. Please contact support if you believe this was done in error.',
	USER_MISSING_PERMISSIONS: 'Your account does not have the permissions necessary to view this page. Please contact support if you need access.'
};


/**
 * Content Validation Errors
 */

export const CONTENT_VALIDATION_ERRORS = {
	IDENTIFYING_INFORMATION: 'identifying_information',
	OFFENSIVE_CONTENT: 'offensive_content'
};


/**
 * Reactions
 */

export const REACTIONS = {
	reaction_a: {
		code: 'reaction_a',
		icon: 'reaction-a-icon',
	},
	reaction_b: {
		code: 'reaction_b',
		icon: 'reaction-b-icon',
	},
	reaction_c: {
		code: 'reaction_c',
		icon: 'reaction-c-icon',
	},
	reaction_d: {
		code: 'reaction_d',
		icon: 'reaction-d-icon',
	},
	reaction_e: {
		code: 'reaction_e',
		icon: 'reaction-e-icon',
	},
};


/**
 * Update Actions
 */

export const UPDATE_ACTIONS = {
	REMOVE_STORY: 'action_remove_story',
	HIDE_STORY: 'action_hide_story',
	SHOW_STORY: 'action_show_story',
	ALLOW_SOCIAL: 'action_allow_social',
	DISALLOW_SOCIAL: 'action_disallow_social',
	ALLOW_EMAIL: 'action_allow_email',
	DISALLOW_EMAIL: 'action_disallow_email',
	ALLOW_PUBLISH_MAIN: 'action_allow_publish_main',
	DISALLOW_PUBLISH_MAIN: 'action_disallow_publish_main',
	ALLOW_NEWSLETTER: 'action_allow_newsletter',
	DISALLOW_NEWSLETTER: 'action_disallow_newsletter',
	UPDATE_EMAIL: 'action_update_email',
	UPDATE_TAGS: 'action_update_tags',
	ENABLE_COMMENTS: 'enable_comments',
	DISABLE_COMMENTS: 'disable_comments'
};


/**
 * Triggers
 */

export const CONTENT_TRIGGERS = {
	SELF_HARM: 'SELF_HARM',
	HOMICIDAL: 'HOMICIDAL',
	DEROGATORY_CONTENT: 'DEROGATORY_CONTENT'
};


/**
 * Story Media Types
 */

export const STORY_MEDIA_TYPES = {
	NARRATIVE: 'narrative',
	ARTWORK: 'artwork'
};


/**
 * Obfuscation Parameters
 */

export const OBFUSCATE_OPEN_CODE = '{~';
export const OBFUSCATE_CLOSE_CODE = '~}';
export const OBFUSCATE_REGEX = /{~.*?~}/g;
export const OBFUSCATE_REGEX_GROUP = /{~(.*?)~}/g;
export const OBFUSCATE_TEXT_REPLACE = '****';
export const EXCERPT_MIN = 250;
export const EXCERPT_MAX = 550;


/**
 * File Upload Limits (Bytes)
 */

export const FILE_UPLOAD_LIMITS = {
	SURVIVOR_MEDIA: 10000000, // 10 MB
	GUESTBOOK_MEDIA: 10000000, // 10 MB
	RESOURCE_MEDIA: 10000000, // 10 MB
};


/**
 * Image Upload Resolution Limits (Pixels)
 */

export const FILE_UPLOAD_RES_LIMITS = {
	SURVIVOR_MEDIA: {
		MIN: 100, // 100 pixels
		MAX: 5000 // 5000 pixels
	},
	GUESTBOOK_MEDIA: {
		MIN: 50, // 50 pixels
		MAX: 2000 // 2000 pixels
	},
	RESOURCE_MEDIA: {
		MIN: 300, // 300 pixels
		MAX: 2000 // 2000 pixels
	}
};


/**
 * Acceptable Image Ratios
 */

export const ACCEPTABLE_IMAGE_RATIOS = {
	SURVIVOR_MEDIA: 2.8, // 1:2.8 (Both width and height)
	GUESTBOOK_MEDIA: 2.8, // 1:2.8 (Both width and height)
	RESOURCE_MEDIA: 2.8 // 1:2.8 (Both width and height)
};


/**
 * Acceptable File Types
 */

export const ACCEPTABLE_FILE_TYPES = {
	SURVIVOR_MEDIA: {
		EXTENSIONS: [
			'png',
			'jpeg',
			'jpg'
		],
		MIME_TYPES: [
			'image/png',
			'image/jpeg',
			'image/jpg'
		]
	},
	GUESTBOOK_MEDIA: {
		EXTENSIONS: [
			'png'
		],
		MIME_TYPES: [
			'image/png'
		]
	},
	RESOURCE_MEDIA: {
		EXTENSIONS: [
			'png',
			'jpeg',
			'jpg'
		],
		MIME_TYPES: [
			'image/png',
			'image/jpeg',
			'image/jpg'
		]
	},
};


/**
 * User Actions
 */

export const USER_ACTIONS = {
	STARTED_GROUNDING_EXERCISE: 'started_grounding_exercise',
	SUPPORT_CARD_CLICK: 'support_card_click',
	SUPPORT_CARD_VIDEO_PLAY: 'support_card_video_play',
	RESOURCE_LINK_CLICK: 'resource_link_click',
	STORY_EXTERNAL_LINK_CLICK: 'story_external_link_click',
	ANSWER_EXTERNAL_LINK_CLICK: 'answer_external_link_click',
	MESSAGE_EXTERNAL_LINK_CLICK: 'story_external_link_click',
	EXTERNAL_LINK_CLICK: 'external_link_click',
	REMOVE_REACTION: 'remove_reaction',
	SHARE_OPTION_CLICK: 'share_option_click',
	FAB_SAFETY_EXIT_CLICK: 'fab_safety_exit_click',
	FAB_RESOURCES_CLICK: 'fab_resources_click',
	SEARCHED_QUESTION_SUGGESTION_CLICK: 'searched_question_suggestion_click'
};


/**
 * Tag Families
 */

export const TAG_FAMILIES = {
	location: {
		id: 'location'
	},
	perpetrator: {
		id: 'perpetrator'
	},
	race: {
		id: 'race'
	},
	orientation: {
		id: 'orientation'
	},
	gender: {
		id: 'gender'
	},
	identification: {
		id: 'identification',
		combineWith: 'gender'
	},
	age: {
		id: 'age'
	},
	experiences: {
		id: 'experiences'
	},
	industry: {
		id: 'industry'
	},
	influence: {
		id: 'influence'
	},
	region: {
		id: 'region'
	}
};


/**
 * Content Types
 */

export const INTERNAL_CONTENT_TYPES = {
	STORY: 'story',
	MESSAGE: 'message',
	MANTRA: 'mantra',
	SUPPORT_IMAGE: 'support_image',
	SUPPORT_VIDEO: 'support_video'
};


/**
 * Message Types
 */

export const MESSAGE_TYPES = {
	HEALING_MESSAGE: 'healing_message',
	HOPE_MESSAGE: 'hope_message',
	SUPPORT_MESSAGE: 'support_message',
};


/**
 * Available Jobs
 */

export const AVAILABLE_JOBS = {

	/**
	 * General
	 */

	UPDATE_STORY_STATUSES: {
		id: 'update_story_statuses',
		name: 'Update Story Statuses'
	},
	HANDLE_SENSITIVE_SESSIONS: {
		id: 'handle_sensitive_user_sessions',
		name: 'Handle Sensitive User Sessions'
	},
	HANDLE_BLOG_AUTOPOST: {
		id: 'handle_blog_autopost',
		name: 'Trigger Blog Autopost'
	},
	GENERATE_QUESTION_HEURISTICS: {
		id: 'generate_question_heuristics',
		name: 'Generate Question Heuristics'
	},
	GENERATE_STORY_HEURISTICS: {
		id: 'generate_story_heuristics',
		name: 'Generate Story Heuristics'
	},
	GENERATE_MESSAGE_HEURISTICS: {
		id: 'generate_message_heuristics',
		name: 'Generate Message Heuristics'
	},
	GENERATE_COMMENT_HEURISTICS: {
		id: 'generate_comment_heuristics',
		name: 'Generate Comment Heuristics'
	},
	GENERATE_FEED_ALGORITHM_SCORES: {
		id: 'generate_feed_algorithm_scores',
		name: 'Generate Feed Algorithm Scores'
	},
	GENERATE_CLEAN_UP_HEURISTICS: {
		id: 'generate_clean_up_heuristics',
		name: 'Generate & Clean Up Heuristics'
	},
	MONITOR_INTERNAL_MILESTONE_UPDATES: {
		id: 'monitor_internal_milestone_updates',
		name: 'Monitor Internal Milestone Updates'
	},

	/**
	 * Review
	 */

	COMPLETE_MESSAGE_REVIEW: {
		id: 'complete_message_review',
		name: 'Complete Message Review'
	},
	COMPLETE_COMMENT_REVIEW: {
		id: 'complete_comment_review',
		name: 'Complete Comment Review'
	},

	/**
	 * Senders
	 */

	SEND_SCHEDULED_MILESTONES: {
		id: 'send_scheduled_milestones',
		name: 'Send Scheduled Milestones'
	},
	SEND_SCHEDULED_STORY_UPDATES: {
		id: 'send_scheduled_story_updates',
		name: 'Send Scheduled Story Updates'
	},
	SEND_SCHEDULED_MESSAGING_UPDATES: {
		id: 'send_scheduled_messaging_updates',
		name: 'Send Scheduled Messaging Updates'
	},

	/**
	 * Count Story Regions
	 */

	COUNT_STORY_REGIONS_CORE: {
		id: 'count_story_regions_core',
		name: 'Count Story Regions (All)',
		data: {}
	},
	COUNT_STORY_REGIONS_SURVIVOR_SPACES: {
		id: 'count_story_regions_survivor_spaces',
		name: 'Count Story Regions (Survivor Spaces)',
		data: {
			partner_id: 'zhwPEbkSgXGgiQbHfHWF',
		}
	},
	COUNT_STORY_REGIONS_NO_MORE: {
		id: 'count_story_regions_no_more',
		name: 'Count Story Regions (NO MORE Silence, Speak Your Truth)',
		data: {
			partner_id: 'Jg7Fq1geWC3saUPPvYwX',
		}
	},
	COUNT_STORY_REGIONS_UNAPOLOGETICALLY_SURVIVING: {
		id: 'count_story_regions_unapologetically_surviving',
		name: 'Count Story Regions (Unapologetically Surviving)',
		data: {
			partner_id: 'RRrWPtLOAJfpQ6ctMFcc',
		}
	},
	COUNT_STORY_REGIONS_WE_SPEAK: {
		id: 'count_story_regions_we_speak',
		name: 'Count Story Regions (We-Speak)',
		data: {
			partner_id: 'gSZUalMBo4EQLBQoJxA5',
		}
	},
};


/**
 * Available Jobs
 */

export const AVAILABLE_AUDIT_TASKS = {
	AUDIT_GLOBAL_DIRECTORY: {
		id: 'audit_global_directory',
		name: 'Audit Global Directory'
	}
};


/**
 * Job Status Options
 */

export const JOB_STATUS_OPTIONS = {
	STARTED: 'started',
	COMPLETED: 'completed'
};


/**
 * Review Tags
 */

export const REVIEW_TAGS = {
	SUICIDALITY: {
		id: 'SUICIDALITY',
		name: 'Suicidality Potential'
	},
	HOMICIDAILTY: {
		id: 'HOMICIDAILTY',
		name: 'Homicidality Potential'
	},
	TRIGGERING: {
		id: 'TRIGGERING',
		name: 'Trigger Potential'
	},
	MINOR: {
		id: 'MINOR',
		name: 'Subject Is Potentially A Minor'
	},
	PERPETRATOR: {
		id: 'PERPETRATOR',
		name: 'Subject Is Potentially a Perpetrator'
	},
	PAST_MINOR: {
		id: 'PAST_MINOR',
		name: 'Subject Was Potentially A Minor'
	},
	STORY_NOT_APPROPRIATE: {
		id: 'STORY_NOT_APPROPRIATE',
		name: 'Not Appropriate'
	},
	STORY_NOT_APPLICABLE: {
		id: 'STORY_NOT_APPLICABLE',
		name: 'Not Applicable'
	}
};


/**
 * Admin Emails
 */

export const ADMIN_EMAILS = ['hello@ourwave.org'];


/**
 * Core Domain Whitelist
 */

export const CORE_DOMAIN_WHITELIST = () => [
	'https://ourwave.org',
	'https://*.ourwave.org',
	'https://project-dashboard-943504098534.herokuapp.com',
	'https://our-wave-*.herokuapp.com',
	process.env.ENV === 'development' && [
		'http://localhost:*'
	]
].filter(Boolean);


/**
 * Path Blacklist (Movement)
 */

export const PATH_BLACKLIST = [
	'/locales/*',
	'/locales/**/*',
	'/robots.txt',
	'/manifest.json',
	`${process.env.PARSE_MOUNT}/*`,
	`${process.env.PARSE_MOUNT}/**/*`,
	'/sitemap*',
	'/sitemap/**/*',
	'/static/*',
	'/static/**/*',
	'/public/*',
	'/public/**/*',
	'/_integrations/*',
	'/_integrations/**/*',
	'/api*',
	'/api/**/*',
	'/api/*',
	'/api/**/*',
	'/api/v1/*',
	'/api/v1/**/*',
	'/_api/*',
	'/_api/**/*',
	'/logout',
	'/fb52fc6a64a34f06b05924e069dca721.txt',
	'/.*',
	'/.well-known*',
	'.well-known*',
	'/.well-known/*',
	'.well-known/*',
	'/.well-known/**/*',
	'.well-known/**/*',
	'/wp-admin*',
	'/wp-login*',
	'/wp-admin/*',
	'/wp-login/*',
	'/wp-admin/**/*',
	'/wp-login/**/*',
	'/xmlrpc*',
	'/xmlrpc/**/*',
	'/_external/link*',
	'/wordpress'
];


/**
 * Milestone Types
 */

export const SURVIVOR_MILESTONE_TYPES = {
	NEW_REACTION: 'reaction_new',
	REACTIONS_5: 'reaction_5',
	REACTIONS_10: 'reaction_10',
	REACTIONS_20: 'reaction_20',
	REACTIONS_50: 'reaction_50',
	REACTIONS_100: 'reaction_100',
	REACTIONS_MULTIPLE_100: 'reaction_multiple_100',
	FIRST_READ: 'first_read',
	READS_10: 'reads_10',
	READS_25: 'reads_25',
	READS_50: 'reads_50',
	READS_100: 'reads_100',
	READS_MULTIPLE_100: 'reads_multiple_100',
};


/**
 * Internal Milestone Types
 */

export const INTERNAL_MILESTONE_TYPES = {
	VIEW: 'view'
};


/**
 * State Options
 */

export const STATE_OPTIONS = [
	{
		name: 'Alabama',
		abbreviation: 'AL'
	},
	{
		name: 'Alaska',
		abbreviation: 'AK'
	},
	{
		name: 'American Samoa',
		abbreviation: 'AS'
	},
	{
		name: 'Arizona',
		abbreviation: 'AZ'
	},
	{
		name: 'Arkansas',
		abbreviation: 'AR'
	},
	{
		name: 'California',
		abbreviation: 'CA'
	},
	{
		name: 'Colorado',
		abbreviation: 'CO'
	},
	{
		name: 'Connecticut',
		abbreviation: 'CT'
	},
	{
		name: 'Delaware',
		abbreviation: 'DE'
	},
	{
		name: 'District Of Columbia',
		abbreviation: 'DC'
	},
	{
		name: 'Federated States Of Micronesia',
		abbreviation: 'FM'
	},
	{
		name: 'Florida',
		abbreviation: 'FL'
	},
	{
		name: 'Georgia',
		abbreviation: 'GA'
	},
	{
		name: 'Guam',
		abbreviation: 'GU'
	},
	{
		name: 'Hawaii',
		abbreviation: 'HI'
	},
	{
		name: 'Idaho',
		abbreviation: 'ID'
	},
	{
		name: 'Illinois',
		abbreviation: 'IL'
	},
	{
		name: 'Indiana',
		abbreviation: 'IN'
	},
	{
		name: 'Iowa',
		abbreviation: 'IA'
	},
	{
		name: 'Kansas',
		abbreviation: 'KS'
	},
	{
		name: 'Kentucky',
		abbreviation: 'KY'
	},
	{
		name: 'Louisiana',
		abbreviation: 'LA'
	},
	{
		name: 'Maine',
		abbreviation: 'ME'
	},
	{
		name: 'Marshall Islands',
		abbreviation: 'MH'
	},
	{
		name: 'Maryland',
		abbreviation: 'MD'
	},
	{
		name: 'Massachusetts',
		abbreviation: 'MA'
	},
	{
		name: 'Michigan',
		abbreviation: 'MI'
	},
	{
		name: 'Minnesota',
		abbreviation: 'MN'
	},
	{
		name: 'Mississippi',
		abbreviation: 'MS'
	},
	{
		name: 'Missouri',
		abbreviation: 'MO'
	},
	{
		name: 'Montana',
		abbreviation: 'MT'
	},
	{
		name: 'Nebraska',
		abbreviation: 'NE'
	},
	{
		name: 'Nevada',
		abbreviation: 'NV'
	},
	{
		name: 'New Hampshire',
		abbreviation: 'NH'
	},
	{
		name: 'New Jersey',
		abbreviation: 'NJ'
	},
	{
		name: 'New Mexico',
		abbreviation: 'NM'
	},
	{
		name: 'New York',
		abbreviation: 'NY'
	},
	{
		name: 'North Carolina',
		abbreviation: 'NC'
	},
	{
		name: 'North Dakota',
		abbreviation: 'ND'
	},
	{
		name: 'Northern Mariana Islands',
		abbreviation: 'MP'
	},
	{
		name: 'Ohio',
		abbreviation: 'OH'
	},
	{
		name: 'Oklahoma',
		abbreviation: 'OK'
	},
	{
		name: 'Oregon',
		abbreviation: 'OR'
	},
	{
		name: 'Palau',
		abbreviation: 'PW'
	},
	{
		name: 'Pennsylvania',
		abbreviation: 'PA'
	},
	{
		name: 'Puerto Rico',
		abbreviation: 'PR'
	},
	{
		name: 'Rhode Island',
		abbreviation: 'RI'
	},
	{
		name: 'South Carolina',
		abbreviation: 'SC'
	},
	{
		name: 'South Dakota',
		abbreviation: 'SD'
	},
	{
		name: 'Tennessee',
		abbreviation: 'TN'
	},
	{
		name: 'Texas',
		abbreviation: 'TX'
	},
	{
		name: 'Utah',
		abbreviation: 'UT'
	},
	{
		name: 'Vermont',
		abbreviation: 'VT'
	},
	{
		name: 'Virgin Islands',
		abbreviation: 'VI'
	},
	{
		name: 'Virginia',
		abbreviation: 'VA'
	},
	{
		name: 'Washington',
		abbreviation: 'WA'
	},
	{
		name: 'West Virginia',
		abbreviation: 'WV'
	},
	{
		name: 'Wisconsin',
		abbreviation: 'WI'
	},
	{
		name: 'Wyoming',
		abbreviation: 'WY'
	}
];


/**
 * Country Options
 */

export const COUNTRY_OPTIONS = [
	{ name: 'United States', code: 'US' },
	{ name: 'Afghanistan', code: 'AF' },
	{ name: 'Åland Islands', code: 'AX' },
	{ name: 'Albania', code: 'AL' },
	{ name: 'Algeria', code: 'DZ' },
	{ name: 'American Samoa', code: 'AS' },
	{ name: 'AndorrA', code: 'AD' },
	{ name: 'Angola', code: 'AO' },
	{ name: 'Anguilla', code: 'AI' },
	{ name: 'Antarctica', code: 'AQ' },
	{ name: 'Antigua and Barbuda', code: 'AG' },
	{ name: 'Argentina', code: 'AR' },
	{ name: 'Armenia', code: 'AM' },
	{ name: 'Aruba', code: 'AW' },
	{ name: 'Australia', code: 'AU' },
	{ name: 'Austria', code: 'AT' },
	{ name: 'Azerbaijan', code: 'AZ' },
	{ name: 'Bahamas', code: 'BS' },
	{ name: 'Bahrain', code: 'BH' },
	{ name: 'Bangladesh', code: 'BD' },
	{ name: 'Barbados', code: 'BB' },
	{ name: 'Belarus', code: 'BY' },
	{ name: 'Belgium', code: 'BE' },
	{ name: 'Belize', code: 'BZ' },
	{ name: 'Benin', code: 'BJ' },
	{ name: 'Bermuda', code: 'BM' },
	{ name: 'Bhutan', code: 'BT' },
	{ name: 'Bolivia', code: 'BO' },
	{ name: 'Bosnia and Herzegovina', code: 'BA' },
	{ name: 'Botswana', code: 'BW' },
	{ name: 'Bouvet Island', code: 'BV' },
	{ name: 'Brazil', code: 'BR' },
	{ name: 'British Indian Ocean Territory', code: 'IO' },
	{ name: 'Brunei Darussalam', code: 'BN' },
	{ name: 'Bulgaria', code: 'BG' },
	{ name: 'Burkina Faso', code: 'BF' },
	{ name: 'Burundi', code: 'BI' },
	{ name: 'Cambodia', code: 'KH' },
	{ name: 'Cameroon', code: 'CM' },
	{ name: 'Canada', code: 'CA' },
	{ name: 'Cape Verde', code: 'CV' },
	{ name: 'Cayman Islands', code: 'KY' },
	{ name: 'Central African Republic', code: 'CF' },
	{ name: 'Chad', code: 'TD' },
	{ name: 'Chile', code: 'CL' },
	{ name: 'China', code: 'CN' },
	{ name: 'Christmas Island', code: 'CX' },
	{ name: 'Cocos (Keeling) Islands', code: 'CC' },
	{ name: 'Colombia', code: 'CO' },
	{ name: 'Comoros', code: 'KM' },
	{ name: 'Congo', code: 'CG' },
	{ name: 'Congo, The Democratic Republic of the', code: 'CD' },
	{ name: 'Cook Islands', code: 'CK' },
	{ name: 'Costa Rica', code: 'CR' },
	{ name: 'Croatia', code: 'HR' },
	{ name: 'Cuba', code: 'CU' },
	{ name: 'Cyprus', code: 'CY' },
	{ name: 'Czech Republic', code: 'CZ' },
	{ name: 'Denmark', code: 'DK' },
	{ name: 'Djibouti', code: 'DJ' },
	{ name: 'Dominica', code: 'DM' },
	{ name: 'Dominican Republic', code: 'DO' },
	{ name: 'Ecuador', code: 'EC' },
	{ name: 'Egypt', code: 'EG' },
	{ name: 'El Salvador', code: 'SV' },
	{ name: 'Equatorial Guinea', code: 'GQ' },
	{ name: 'Eritrea', code: 'ER' },
	{ name: 'Estonia', code: 'EE' },
	{ name: 'Ethiopia', code: 'ET' },
	{ name: 'Falkland Islands (Malvinas)', code: 'FK' },
	{ name: 'Faroe Islands', code: 'FO' },
	{ name: 'Fiji', code: 'FJ' },
	{ name: 'Finland', code: 'FI' },
	{ name: 'France', code: 'FR' },
	{ name: 'French Guiana', code: 'GF' },
	{ name: 'French Polynesia', code: 'PF' },
	{ name: 'French Southern Territories', code: 'TF' },
	{ name: 'Gabon', code: 'GA' },
	{ name: 'Gambia', code: 'GM' },
	{ name: 'Georgia', code: 'GE' },
	{ name: 'Germany', code: 'DE' },
	{ name: 'Ghana', code: 'GH' },
	{ name: 'Gibraltar', code: 'GI' },
	{ name: 'Greece', code: 'GR' },
	{ name: 'Greenland', code: 'GL' },
	{ name: 'Grenada', code: 'GD' },
	{ name: 'Guadeloupe', code: 'GP' },
	{ name: 'Guam', code: 'GU' },
	{ name: 'Guatemala', code: 'GT' },
	{ name: 'Guernsey', code: 'GG' },
	{ name: 'Guinea', code: 'GN' },
	{ name: 'Guinea-Bissau', code: 'GW' },
	{ name: 'Guyana', code: 'GY' },
	{ name: 'Haiti', code: 'HT' },
	{ name: 'Heard Island and Mcdonald Islands', code: 'HM' },
	{ name: 'Holy See (Vatican City State)', code: 'VA' },
	{ name: 'Honduras', code: 'HN' },
	{ name: 'Hong Kong', code: 'HK' },
	{ name: 'Hungary', code: 'HU' },
	{ name: 'Iceland', code: 'IS' },
	{ name: 'India', code: 'IN' },
	{ name: 'Indonesia', code: 'ID' },
	{ name: 'Iran, Islamic Republic Of', code: 'IR' },
	{ name: 'Iraq', code: 'IQ' },
	{ name: 'Ireland', code: 'IE' },
	{ name: 'Isle of Man', code: 'IM' },
	{ name: 'Israel', code: 'IL' },
	{ name: 'Italy', code: 'IT' },
	{ name: 'Jamaica', code: 'JM' },
	{ name: 'Japan', code: 'JP' },
	{ name: 'Jersey', code: 'JE' },
	{ name: 'Jordan', code: 'JO' },
	{ name: 'Kazakhstan', code: 'KZ' },
	{ name: 'Kenya', code: 'KE' },
	{ name: 'Kiribati', code: 'KI' },
	{ name: 'Korea, Republic of', code: 'KR' },
	{ name: 'Kuwait', code: 'KW' },
	{ name: 'Kyrgyzstan', code: 'KG' },
	{ name: 'Latvia', code: 'LV' },
	{ name: 'Lebanon', code: 'LB' },
	{ name: 'Lesotho', code: 'LS' },
	{ name: 'Liberia', code: 'LR' },
	{ name: 'Libyan Arab Jamahiriya', code: 'LY' },
	{ name: 'Liechtenstein', code: 'LI' },
	{ name: 'Lithuania', code: 'LT' },
	{ name: 'Luxembourg', code: 'LU' },
	{ name: 'Macao', code: 'MO' },
	{ name: 'North Macedonia', code: 'MK' },
	{ name: 'Madagascar', code: 'MG' },
	{ name: 'Malawi', code: 'MW' },
	{ name: 'Malaysia', code: 'MY' },
	{ name: 'Maldives', code: 'MV' },
	{ name: 'Mali', code: 'ML' },
	{ name: 'Malta', code: 'MT' },
	{ name: 'Marshall Islands', code: 'MH' },
	{ name: 'Martinique', code: 'MQ' },
	{ name: 'Mauritania', code: 'MR' },
	{ name: 'Mauritius', code: 'MU' },
	{ name: 'Mayotte', code: 'YT' },
	{ name: 'Mexico', code: 'MX' },
	{ name: 'Micronesia, Federated States of', code: 'FM' },
	{ name: 'Moldova, Republic of', code: 'MD' },
	{ name: 'Monaco', code: 'MC' },
	{ name: 'Mongolia', code: 'MN' },
	{ name: 'Montserrat', code: 'MS' },
	{ name: 'Morocco', code: 'MA' },
	{ name: 'Mozambique', code: 'MZ' },
	{ name: 'Myanmar', code: 'MM' },
	{ name: 'Namibia', code: 'NA' },
	{ name: 'Nauru', code: 'NR' },
	{ name: 'Nepal', code: 'NP' },
	{ name: 'Netherlands', code: 'NL' },
	{ name: 'Netherlands Antilles', code: 'AN' },
	{ name: 'New Caledonia', code: 'NC' },
	{ name: 'New Zealand', code: 'NZ' },
	{ name: 'Nicaragua', code: 'NI' },
	{ name: 'Niger', code: 'NE' },
	{ name: 'Nigeria', code: 'NG' },
	{ name: 'Niue', code: 'NU' },
	{ name: 'Norfolk Island', code: 'NF' },
	{ name: 'Northern Mariana Islands', code: 'MP' },
	{ name: 'Norway', code: 'NO' },
	{ name: 'Oman', code: 'OM' },
	{ name: 'Pakistan', code: 'PK' },
	{ name: 'Palau', code: 'PW' },
	{ name: 'Palestinian Territory, Occupied', code: 'PS' },
	{ name: 'Panama', code: 'PA' },
	{ name: 'Papua New Guinea', code: 'PG' },
	{ name: 'Paraguay', code: 'PY' },
	{ name: 'Peru', code: 'PE' },
	{ name: 'Philippines', code: 'PH' },
	{ name: 'Pitcairn Islands', code: 'PN' },
	{ name: 'Poland', code: 'PL' },
	{ name: 'Portugal', code: 'PT' },
	{ name: 'Puerto Rico', code: 'PR' },
	{ name: 'Qatar', code: 'QA' },
	{ name: 'Reunion', code: 'RE' },
	{ name: 'Romania', code: 'RO' },
	{ name: 'Russian Federation', code: 'RU' },
	{ name: 'Rwanda', code: 'RW' },
	{ name: 'Saint Helena', code: 'SH' },
	{ name: 'Saint Kitts and Nevis', code: 'KN' },
	{ name: 'Saint Lucia', code: 'LC' },
	{ name: 'Saint Pierre and Miquelon', code: 'PM' },
	{ name: 'Saint Vincent and the Grenadines', code: 'VC' },
	{ name: 'Samoa', code: 'WS' },
	{ name: 'San Marino', code: 'SM' },
	{ name: 'Sao Tome and Principe', code: 'ST' },
	{ name: 'Saudi Arabia', code: 'SA' },
	{ name: 'Senegal', code: 'SN' },
	{ name: 'Serbia and Montenegro', code: 'CS' },
	{ name: 'Seychelles', code: 'SC' },
	{ name: 'Sierra Leone', code: 'SL' },
	{ name: 'Singapore', code: 'SG' },
	{ name: 'Slovakia', code: 'SK' },
	{ name: 'Slovenia', code: 'SI' },
	{ name: 'Solomon Islands', code: 'SB' },
	{ name: 'Somalia', code: 'SO' },
	{ name: 'South Africa', code: 'ZA' },
	{ name: 'South Georgia and the South Sandwich Islands', code: 'GS' },
	{ name: 'Spain', code: 'ES' },
	{ name: 'Sri Lanka', code: 'LK' },
	{ name: 'Sudan', code: 'SD' },
	{ name: 'Suriname', code: 'SR' },
	{ name: 'Svalbard and Jan Mayen', code: 'SJ' },
	{ name: 'Swaziland', code: 'SZ' },
	{ name: 'Sweden', code: 'SE' },
	{ name: 'Switzerland', code: 'CH' },
	{ name: 'Syrian Arab Republic', code: 'SY' },
	{ name: 'Taiwan', code: 'TW' },
	{ name: 'Tajikistan', code: 'TJ' },
	{ name: 'Tanzania, United Republic of', code: 'TZ' },
	{ name: 'Thailand', code: 'TH' },
	{ name: 'Timor-Leste', code: 'TL' },
	{ name: 'Togo', code: 'TG' },
	{ name: 'Tokelau', code: 'TK' },
	{ name: 'Tonga', code: 'TO' },
	{ name: 'Trinidad and Tobago', code: 'TT' },
	{ name: 'Tunisia', code: 'TN' },
	{ name: 'Turkey', code: 'TR' },
	{ name: 'Turkmenistan', code: 'TM' },
	{ name: 'Turks and Caicos Islands', code: 'TC' },
	{ name: 'Tuvalu', code: 'TV' },
	{ name: 'Uganda', code: 'UG' },
	{ name: 'Ukraine', code: 'UA' },
	{ name: 'United Arab Emirates', code: 'AE' },
	{ name: 'United Kingdom', code: 'GB' },
	{ name: 'United States Minor Outlying Islands', code: 'UM' },
	{ name: 'Uruguay', code: 'UY' },
	{ name: 'Uzbekistan', code: 'UZ' },
	{ name: 'Vanuatu', code: 'VU' },
	{ name: 'Venezuela', code: 'VE' },
	{ name: 'Vietnam', code: 'VN' },
	{ name: 'Virgin Islands, British', code: 'VG' },
	{ name: 'Virgin Islands, U.S.', code: 'VI' },
	{ name: 'Wallis and Futuna', code: 'WF' },
	{ name: 'Western Sahara', code: 'EH' },
	{ name: 'Yemen', code: 'YE' },
	{ name: 'Zambia', code: 'ZM' },
	{ name: 'Zimbabwe', code: 'ZN' }
];


/**
 * Language Options
 */

export const LANGUAGE_OPTIONS = [
	{ name: 'English', code: 'en' },
	{ name: 'Español', code: 'es' },
	{ name: '日本語', code: 'ja' },
];


/**
 * Full Stop Punctuation
 */

export const FULL_STOP_PUNCTUATION = [
	'!',
	'.',
	'?',
	'։',
	'؟',
	'۔',
	'܀',
	'܁',
	'܂',
	'߹',
	'।',
	'॥',
	'၊',
	'။',
	'።',
	'፧',
	'፨',
	'᙮',
	'᠃',
	'᠉',
	'᥄',
	'᥅',
	'᪨',
	'᪩',
	'᪪',
	'᪫',
	'᭚',
	'᭛',
	'᭞',
	'᭟',
	'᰻',
	'᰼',
	'᱾',
	'᱿',
	'‼',
	'‽',
	'⁇',
	'⁈',
	'⁉',
	'⸮',
	'。',
	'꓿',
	'꘎',
	'꘏',
	'꛳',
	'꛷',
	'꡶',
	'꡷',
	'꣎',
	'꣏',
	'꤯',
	'꧈',
	'꧉',
	'꩝',
	'꩞',
	'꩟',
	'꫰',
	'꫱',
	'꯫',
	'﹒',
	'﹖',
	'﹗',
	'！',
	'．',
	'？',
	'｡',
	'𑁇',
	'𑁈',
	'𑂾',
	'𑂿',
	'𑃀',
	'𑃁',
	'𑅁',
	'𑅂',
	'𑅃',
	'𑇅',
	'𑇆',
];


/**
 * Admin Dashboard Sections
 */

export const ADMIN_DASHBOARD_SECTIONS = {
	STORY_REVIEW: 'story_review',
	STORY_TAGS: 'story_tags',
	PLATFORM_METRICS: 'platform_metrics',
	VISITOR_METRICS: 'visitor_metrics',
	RECENT_ACTIVITY: 'recent_activity'
};


/**
 * Momentary Assessment Question Types
 */

export const MOMENTARY_ASSESSMENT_QUESTION_TYPES = {
	POLAR: 'polar',
	MULTIPLE_CHOICE: 'multiple_choice'
};
